import { Box, Grid, InputAdornment } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/**API */
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';
import { getBranch } from 'utils/app.utils';

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import Loading from 'component/Loading';
import TreatmentCourseList from './TreatmentCourseList';

/**STYLE */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';

import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import DropdownButton from 'new-components/buttons/dropdown-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import { UseSnCode } from 'utils/useSnCode';
import ExportToExcel from './ExportToExcel';

export default function TreatmentCourse() {
    const { t } = useTranslation();

    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);

    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');


    const [treatmentRightId, setTreatmentRightId] = useState<number>(0);

    const [treatmentCourseData, setTreatmentCourseData] = useState<any[]>([]);
    const [courseType, setCourseType] = useState<any[]>([]);
    const [courseTypeSelected, setCourseTypeSelected] = useState<any>('all');
    const [courseItem, setCourseItem] = useState<any[]>([]);
    const [courseItemSelected, setCourseItemSelected] = useState<any>('all');

    const [treatmentCourseExport, setTreatmentCourseExport] = useState<any[]>([]);
    const [treatmentCoursePage, setTreatmentCoursePage] = useState<number>(1);
    const [treatmentCourseLimitPage, setTreatmentCourseLimitPage] = useState<number>(10);

    const [search, setSearch] = useState<string>('');
    const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const momentDate = () => momentNowRange
    const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
    const [fetchData, setFetchData] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true);
    const [snType, setSnType] = useState(false);

    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode()
            setSnType(sn)
        }

        fetchData()
    }, [snType])

    useEffect(() => {
        handleSnCode()
    }, [])

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'TREATMENT_COURES') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    const fetchTreatmentCourse = useCallback(async () => {
        await verifyPermission()
        let condition: FindAllReportInterface = {
            page: treatmentCoursePage, pageLimit: treatmentCourseLimitPage,
            branchId: branchId, treatmentRightId: treatmentRightId
        }
        if (rangeDate) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
        if (courseTypeSelected !== 'all') condition = { ...condition, courseType: courseTypeSelected }
        if (courseItemSelected !== 'all') condition = { ...condition, courseName: courseItemSelected }
        if (search) condition = { ...condition, search: search }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }

        ReportApi.reportTreatmentCourse(condition)
            .then(({ headers, data }) => {
                setTreatmentCourseData(data.rows)
                setCourseType(data.courses)
                setCourseItem(data.courseItems)
                if (data.courses.length === 0) {
                    setCourseTypeSelected('all')
                }
                if (data.courseItems.length === 0) {
                    setCourseItemSelected('all')
                }

                setRowCount(headers['x-total'])
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))
        setFetchData(false)
    }, [treatmentCoursePage, treatmentCourseLimitPage, sortBy, sortType, treatmentRightId, fetchData])

    const fetchPatientInformationExport = useCallback(async () => {
        let condition: FindAllReportInterface = {
            page: 1, pageLimit: 1000,
            branchId: branchId,
            treatmentRightId: treatmentRightId,
            export: 1
        }
        if (search) condition = { ...condition, search: search }
        if (rangeDate) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
        if (courseTypeSelected !== 'all') condition = { ...condition, courseType: courseTypeSelected }
        if (courseItemSelected !== 'all') condition = { ...condition, courseName: courseItemSelected }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }

        ReportApi.reportTreatmentCourse(condition)
            .then(({ data }) => {
                setTreatmentCourseExport(data.rows)
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))

    }, [treatmentCoursePage, treatmentCourseLimitPage, sortBy, sortType, treatmentRightId, fetchData])

    const exportExcel = async () => {
        setLoading(true);
        try {
            await ExportToExcel({
                data: treatmentCourseExport,
                branches,
                branchId,
                filter: {
                    courseType: courseTypeSelected === 'all' ? t('REPORT.REPORT_GROUP.ALL') : courseTypeSelected,
                },
                filterDateStart: rangeDate[0],
                filterDateEnd: rangeDate[1],
                snType: snType,
                t,
            })
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        } finally {
            setLoading(false);
        }
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setTreatmentCourseLimitPage(limit)

    const handleChangePage = (val: number) => setTreatmentCoursePage(val)

    /** UseEffect */
    useEffect(() => {
        fetchTreatmentCourse()
    }, [fetchTreatmentCourse])

    useEffect(() => {
        fetchPatientInformationExport()
    }, [fetchPatientInformationExport])



    return (
        <Box sx={{ height: '100%' }}>
            <HeaderCard text={t('REPORT.REPORT_NAME.TREATMENT_COURES')} />
            <Box sx={{ px: 3, pt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            onChange={(e) => setSearch(e.target.value)}
                            fullWidth
                            label={t('REPORT.REPORT_NAME.SEARCH_TREATMENT_COURES')}
                            value={search}
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.FILTER.BRANCH')}
                            value={branchId}
                            onChange={e => {
                                setBranchId(Number(e.target.value));
                            }}
                            options={[
                                { value: 0, name: t(`REPORT.FILTER.ALL`) },
                                ...branches.map(b => ({
                                    value: b.branchId,
                                    name: b.branchName || b.branchNameEn || ''
                                }))]}
                            width="100%"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.FILTER.COURSE_NAME')}
                            value={courseTypeSelected}
                            onChange={e => setCourseTypeSelected(e.target.value)}
                            options={[
                                { value: 'all', name: t('REPORT.REPORT_GROUP.ALL') },
                                ...courseType.map(b => ({
                                    value: b.treatmentCourseName,
                                    name: b.treatmentCourseName,
                                })),
                            ]}
                            width="100%"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate || emptyRangePicker}
                            onchange={val => {
                                if (_.isEmpty(val)) setRangeDate(emptyRangePicker);
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')]);
                            }}
                            label=""
                        />
                    </Grid>
                    <Grid item xs={12} xl={4}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                onClick={() => {
                                    setFetchData(true);
                                    setLoading(true);
                                    setTreatmentCoursePage(1);
                                }}
                                type={'primary'}
                                variant={'contained'}
                                style={{ width: '120px' }}
                            />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    exportExcel();
                                } else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);

                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>

                <Box marginTop="16px">
                    <TreatmentCourseList
                        filter={{
                            branch: _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn') || '',
                            courseType: courseTypeSelected === 'all' ? t('REPORT.REPORT_GROUP.ALL') : courseTypeSelected,
                            courseItem: courseItemSelected === 'all' ? t('REPORT.REPORT_GROUP.ALL') : courseItemSelected,
                            rangeDate,
                        }}
                        page={treatmentCoursePage}
                        pageLimit={treatmentCourseLimitPage}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={treatmentCourseData}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                        exportData={treatmentCourseExport}
                    />
                </Box>
            </Box>
            <Loading show={loading} type="fullLoading" />
        </Box >
    );

}